import { useEffect, useState } from 'react'
import { subscribeBakeEvents } from '../lib/bakeStatusStore'

/**
 * BakeNotifications — global, selection-independent toasts for IBL bake
 * completion (ENG-561). The bake runs server-side and can finish long after the
 * user has switched to a different environment or character; the per-env chip
 * would never show that "done", so completion is announced here instead.
 *
 * Mounted once at the app root. Subscribes to bakeStatusStore lifecycle events
 * ({ assetId, name, kind: 'ready' | 'error' }) and stacks auto-dismissing
 * toasts. No React state on any hot path — this only reacts to one-shot events.
 */

const TOAST_MS = 6000

export default function BakeNotifications() {
  const [toasts, setToasts] = useState([]) // { id, name, kind, what }

  useEffect(() => {
    let seq = 0
    return subscribeBakeEvents(({ name, kind, what }) => {
      const id = ++seq + ':' + Date.now()
      setToasts(t => [...t, { id, name, kind, what }])
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), TOAST_MS)
    })
  }, [])

  if (!toasts.length) return null

  return (
    <div style={{
      position: 'absolute', bottom: 16, right: 16, zIndex: 40,
      display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end',
      pointerEvents: 'none',
    }}>
      {toasts.map(t => {
        const ok = t.kind === 'ready'
        const accent = ok ? '#22c55e' : '#f87171'
        return (
          <div key={t.id} style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '9px 13px', borderRadius: 9,
            background: 'rgba(15,15,18,0.94)', border: `1px solid ${accent}55`,
            borderLeft: `3px solid ${accent}`,
            color: '#e5e7eb', font: '500 12px/1.3 var(--m-font)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)', maxWidth: 320,
            animation: 'bake-toast-in 0.25s ease-out',
          }}>
            <span style={{ fontSize: 14, color: accent }}>{ok ? '✓' : '⚠'}</span>
            <span>
              {/* Name the artifact this bake produced — a navmesh error
                 announced as a lighting failure sends the user debugging the
                 wrong feature. `what` comes from the bake event ('lighting' |
                 'navmesh'; absent on pre-kind events → lighting wording). */}
              {t.what === 'navmesh'
                ? (ok
                  ? <>Navmesh built for <b>{t.name}</b></>
                  : <>Navmesh build failed for <b>{t.name}</b></>)
                : (ok
                  ? <>Lighting baked for <b>{t.name}</b></>
                  : <>Lighting bake failed for <b>{t.name}</b></>)}
            </span>
          </div>
        )
      })}
      <style>{`@keyframes bake-toast-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }`}</style>
    </div>
  )
}
